import { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { C } from './design.js';
import { sb } from './supabaseClient.js';
import { snapStore } from './offlineStore.js';
import { getAuthItem, setAuthItem, clearAuth } from './authStorage.js';
import { Toaster } from './components/Toast.js';
import { ConfirmModal, LogoLoader, MARCA } from './components/UI.js';
import { outbox } from './offlineQueue.js';
import { textoSalida, tuyasPorSubir } from './components/capturas.logic.js';
import { leerPerfilConLimite } from './acceso/perfil.js';
import { decidirPerfil, subDelToken } from './acceso/acceso.logic.js';
import { ARRANQUE_NORMAL, decidirArranque, esRutaRestablecer } from './acceso/pantalla.logic.js';
import { esRutaPermiso } from './acceso/permiso.logic.js';
// GEOBAJA App Entry Point

// ── Code splitting de primer nivel: el shell completo y cada ruta pública
//    viven en chunks propios. Quien abre /verificar/:id (QR público) ya no
//    descarga la app administrativa entera, y viceversa. La pantalla de entrada
//    también (2026-09-16): su hook, sus campos y el paisaje no viajan con el QR ni
//    con la sesión restaurada.
const lz = (importer, name) => lazy(() => importer().then(m => ({ default: m[name] })));
const LoginScreen            = lz(() => import('./LoginScreen.js'),                       'LoginScreen');
const PermisoAcceso          = lz(() => import('./acceso/PermisoAcceso.js'),              'PermisoAcceso');
const AppShell               = lz(() => import('./AppShell.js'),                          'AppShell');
const ClientePortal          = lz(() => import('./modules/ClientePortal.js'),             'ClientePortal');
const PortalContratista      = lz(() => import('./modules/contratista/PortalContratista.js'), 'PortalContratista');
const VerificarReporte       = lz(() => import('./VerificarReporte.js'),                  'VerificarReporte');
const TarjetaFirmante        = lz(() => import('./TarjetaFirmante.js'),                   'TarjetaFirmante');
const AcuseEntrega           = lz(() => import('./AcuseEntrega.js'),                      'AcuseEntrega');
const PanelDescargas         = lz(() => import('./PanelDescargas.js'),                    'PanelDescargas');
const PdfPreviewCompactacion = lz(() => import('./modules/compactacion/PdfPreview.js'),   'PdfPreviewCompactacion');
const PdfPreviewPresupuesto  = lz(() => import('./modules/presupuestos/PdfPreview.js'),   'PdfPreviewPresupuesto');

// Splash compartido: arranque de sesión y fallback de Suspense de primer nivel.
// La app arranca con la marca que barre, sola, al centro de toda la ventana
// (canon 2026-09-22, lienzo "Esperar en calma"): la única pantalla que no
// hablaba el lenguaje de luz era esta (un spinner de 14 px y el logo apagado).
// El tema de quien entra ya está en <html data-tema> desde el primer cuadro
// (el script de index.html), así que de noche no destella en claro.
function Splash() {
  return (
    <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:C.bg}}>
      <LogoLoader size={MARCA.ventana} label="Entrando…" />
    </div>
  );
}

// Usuario sintético del modo demo del portal cliente (/portal-demo): el
// portal detecta `demo: true` y carga fixtures locales (cliente/demo.js)
// sin tocar Supabase. Para enseñar el producto sin exponer clientes reales.
const DEMO_USUARIO = { id: 'demo', nombre: 'Cliente', apellido: 'Invitado', rol: 'cliente', demo: true };

// Lo que no debe cruzar de una persona a otra en una tablet compartida: los
// snapshots persistidos y el acuse de avisos de la campana. El outbox (capturas
// pendientes) SÍ sobrevive: cada registro tiene dueño y solo sube con su sesión.
function limpiarRastroDePersona() {
  snapStore.clearAll();
  try { localStorage.removeItem('gb_avisos_vistos'); } catch (e) { /* sin almacenamiento */ }
}

// Una llamada que no se espera no puede dejar un rechazo sin atender.
function sinEsperar(promesa) {
  if (promesa && typeof promesa.catch === 'function') promesa.catch(() => {});
}

function idDelPerfilGuardado() {
  try {
    const p = JSON.parse(getAuthItem('sb_perfil') || 'null');
    return (p && p.id) || null;
  } catch (e) { return null; }
}

// ── Router interno: usa useLocation para reaccionar a cualquier cambio de ruta ─
function AppRouter({ usuario, motivoCierre, inicio, onInicioUsado, onLogin, onLogout }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  // Rutas públicas — accesibles sin login, incluso desde sesión activa
  if (pathname.startsWith('/verificar/')) return <VerificarReporte />;
  if (pathname.startsWith('/firmante/'))  return <TarjetaFirmante />;
  if (pathname.startsWith('/acuse/'))      return <AcuseEntrega />;
  if (pathname.startsWith('/reportes'))    return <PanelDescargas />;
  if (pathname.startsWith('/portal-demo')) {
    return <ClientePortal usuario={DEMO_USUARIO} onLogout={() => { window.location.href = '/'; }} />;
  }

  // /oauth/consent: la vista de permiso del conector de claude.ai (opción A del lienzo,
  // 2026-10-28). Pública como /restablecer pero CON la sesión guardada: quien ya entró
  // decide sin volver a escribir su contraseña; sin sesión, primero entra ahí mismo.
  if (esRutaPermiso(pathname)) {
    return <PermisoAcceso usuario={usuario} motivoCierre={motivoCierre} onLogin={onLogin} onCambiarCuenta={() => onLogout()} />;
  }

  // /restablecer: contraseña nueva (enlace de recuperación) o crear (invitación).
  // Pública y resuelta antes que la sesión guardada (App no la restaura ahí). Ya
  // dentro, la ruta no tiene nada que hacer. La pantalla es la MISMA instancia que
  // la de entrar: al volver a / conserva el correo y el aviso.
  const enRestablecer = esRutaRestablecer(pathname);
  if (enRestablecer && usuario) return <Navigate to="/" replace />;
  if (enRestablecer || !usuario) {
    return (
      <LoginScreen
        onLogin={onLogin}
        motivoCierre={motivoCierre}
        modoInicial={inicio.modoInicial}
        enlace={inicio.enlace}
        onSalirDelEnlace={() => {
          onInicioUsado();
          if (enRestablecer) navigate('/', { replace: true });
        }}
      />
    );
  }

  // DEV: preview en vivo del PDF de compactaciones (requiere login)
  if (pathname.startsWith('/dev/pdf-compactacion')) return <PdfPreviewCompactacion />;
  if (pathname.startsWith('/dev/pdf-presupuesto'))  return <PdfPreviewPresupuesto />;

  // Portal de solo lectura para clientes
  if (usuario.rol === 'cliente') return <ClientePortal usuario={usuario} onLogout={onLogout}/>;

  // Cuarto apartado del contratista (obras en supervisión): nunca monta el AppShell
  if (usuario.rol === 'contratista') return <PortalContratista usuario={usuario} onLogout={onLogout}/>;

  return <AppShell usuario={usuario} onLogout={onLogout}/>;
}

export default function App() {
  // La URL se lee UNA vez y antes de restaurar la sesión guardada: un enlace de
  // recuperación o una invitación no debe abrir la sesión de otra persona que quedó
  // guardada en este equipo (acceso/pantalla.logic.js, decidirArranque).
  const [arranque] = useState(() => (typeof window !== 'undefined' ? decidirArranque(window.location) : ARRANQUE_NORMAL));
  const [inicio, setInicio] = useState(arranque);
  const [usuario, setUsuario] = useState(null);
  const [iniciando, setIniciando] = useState(true);
  // Por qué se cerró la sesión (clase de textoAcceso): sesion_cerrada, por_seguridad,
  // acceso_desactivado o pendiente. null = no se cerró sola.
  const [motivoCierre, setMotivoCierre] = useState(null);
  // Salir con capturas propias sin subir: { tuyas, alSalir } mientras se pregunta. null = no se pregunta.
  const [salida, setSalida] = useState(null);

  // Cierre que decide Auth al renovar (sesión cerrada en otro lado, cuenta
  // bloqueada, token reusado). supabaseClient ya limpió la memoria, el almacén si
  // guardaba esa sesión y el dueño de la cola; aquí se quita lo demás y se dice por qué.
  useEffect(() => {
    sb.setOnSessionExpired((motivo) => {
      sb.setToken(null);
      sb.setRefreshToken(null);
      sb.setUser(null);
      limpiarRastroDePersona();
      setSalida(null);
      setUsuario(null);
      setMotivoCierre(motivo || 'sesion_cerrada');
    });
    return () => sb.setOnSessionExpired(null);
  }, []);

  // Cierra la sesión vigente en este equipo sin esperar al servidor: signOut limpia
  // la memoria y la cola primero y va a la red con scope=local y 4 s de límite (el
  // otro equipo de la persona sigue dentro). El almacén se borra si guarda esta
  // sesión o a esta persona: otra ventana de la app instalada puede tener la de
  // alguien más.
  const cerrarSesionLocal = (motivo = null) => {
    const token = sb.getToken();
    const vigente = sb.getUser();
    const guardado = getAuthItem('sb_token');
    const idGuardado = idDelPerfilGuardado();
    const salida = sb.auth.signOut(token);
    if (salida && typeof salida.catch === 'function') salida.catch(() => {});
    if (!guardado || guardado === token || (vigente && vigente.id && vigente.id === idGuardado)) clearAuth();
    limpiarRastroDePersona();
    sb.setToken(null);
    sb.setRefreshToken(null);
    sb.setUser(null);
    setSalida(null);
    setUsuario(null);
    setMotivoCierre(motivo);
  };

  // Restaurar sesión desde el almacén persistente (optimista) + refrescar token y
  // re-consultar el perfil en background. La re-consulta propaga cambios que
  // el admin haya hecho al perfil (rol, obras_asignadas, activo) sin requerir
  // cerrar sesión: basta con recargar la app.
  useEffect(() => {
    // Los parámetros de Auth salen de la barra y del historial en todas las ramas
    // (y un enlace que cayó en la raíz pasa a /restablecer). Antes de montar el router.
    if (arranque.url !== null) {
      try { window.history.replaceState(null, '', arranque.url); } catch (e) { /* sin history */ }
    }
    // En /restablecer (o con un enlace) no se restaura: la sesión guardada puede ser de otra persona.
    const token    = arranque.restaurarSesion ? getAuthItem("sb_token") : null;
    const guardado = arranque.restaurarSesion ? getAuthItem("sb_perfil") : null;
    let p = null;
    if (token && guardado) {
      try { p = JSON.parse(guardado); } catch (e) { p = null; }
    }
    // Un token de una persona con el perfil de otra (lo dejaba una renovación que escribía
    // sobre la sesión guardada por otra ventana) no se restaura: abriría la app con el menú
    // y el rol de una persona y el JWT de otra.
    const duenoDelToken = subDelToken(token);
    if (p && duenoDelToken && p.id && duenoDelToken !== p.id) p = null;
    if (p) {
      const refresh = getAuthItem("sb_refresh_token");
      sb.setToken(token);
      if (refresh) sb.setRefreshToken(refresh);
      // Perfil aceptado en su último inicio de sesión: la cola lo cuenta como dueño, pero no
      // sube nada hasta que Auth y el perfil respondan (una cuenta bloqueada o desactivada
      // mandaría sus capturas a dead-letter con un JWT que aún no vencía).
      sb.setUser(p, { colaEnEspera: true });
      setUsuario(p);
      const esLaMisma = () => {
        const vigente = sb.getUser();
        return !!(sb.getToken() && vigente && vigente.id === p.id);
      };
      (async () => {
        // rechazado: la renovación ya avisó con su motivo, limpió y soltó la cola. sin_red: la sesión sigue.
        if (refresh && (await sb.refreshSession()) === 'rechazado') return;
        if (!esLaMisma()) return;
        if (!p.id) { sinEsperar(sb.drenarCola()); return; }
        const res = await leerPerfilConLimite(p.id);
        // La sesión pudo cerrarse o cambiar mientras tanto: no se resucita nada.
        if (!esLaMisma()) return;
        const d = decidirPerfil(res);
        // Cuenta deshabilitada o devuelta a pendiente tras el último inicio: fuera, diciendo por qué.
        if (d.estado === 'acceso_desactivado' || d.estado === 'pendiente') {
          cerrarSesionLocal(d.estado);
          return;
        }
        if (d.estado === 'ok') {
          const fresh = d.perfil;
          const merged = {
            ...p,
            ...fresh,
            access_token:  sb.getToken()        || p.access_token,
            refresh_token: sb.getRefreshToken() || p.refresh_token,
            avatar: fresh.avatar || p.avatar,
            color:  fresh.color  || p.color,
          };
          sb.setUser(merged);
          setUsuario(merged);
          setAuthItem("sb_perfil", JSON.stringify(merged));
        }
        // Aceptado, o sin una respuesta que lo niegue (sin red, servicio caído, sin filas): la
        // sesión guardada sigue como estaba y la cola sube lo suyo, como antes.
        sinEsperar(sb.drenarCola());
      })();
    }
    setIniciando(false);
    // Solo al arrancar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // El perfil ya lo aceptó la pantalla de entrada (useAcceso): token activo, dueño de
  // la cola y la cola drenando. Aquí se guarda la sesión.
  const handleLogin = (perfil) => {
    // Otra persona que entra en este equipo (por ejemplo, tras crear su contraseña con
    // la sesión de alguien más guardada): lo de la anterior no se hereda.
    const anterior = idDelPerfilGuardado();
    if (anterior && anterior !== perfil.id) limpiarRastroDePersona();
    snapStore.enable();  // reabrir la compuerta de snapshots cerrada por el logout
    setAuthItem("sb_token",         perfil.access_token);
    setAuthItem("sb_refresh_token", perfil.refresh_token || "");
    setAuthItem("sb_perfil",        JSON.stringify(perfil));
    if (perfil.refresh_token) sb.setRefreshToken(perfil.refresh_token);
    setMotivoCierre(null);
    setInicio(ARRANQUE_NORMAL);
    setUsuario(perfil);
  };

  // Cerrar sesión a pedido de la persona. En una tablet compartida sus capturas sin
  // subir se quedan en el equipo y solo suben con su sesión (offlineQueue.js): si deja
  // alguna, primero se le dice y decide (Salir o Cancelar). `alSalir` (opcional) corre
  // solo cuando de verdad sale, para que quien llama suelte lo suyo (realtime, ruta) sin
  // adelantarse a la respuesta. Los portales pasan el evento del clic: se ignora.
  // Devuelve true si salió ya y false si quedó preguntando.
  const salir = (alSalir) => {
    if (alSalir) {
      try { alSalir(); } catch (e) { /* lo de quien llama no detiene la salida */ }
    }
    cerrarSesionLocal(null);
  };
  const handleLogout = (opciones) => {
    const alSalir = opciones && typeof opciones.alSalir === 'function' ? opciones.alSalir : null;
    const tuyas = tuyasPorSubir(outbox.getStatus());
    if (tuyas > 0) {
      setSalida({ tuyas, alSalir });
      return false;
    }
    salir(alSalir);
    return true;
  };

  if (iniciando) return <Splash />;

  return (
    <BrowserRouter>
      <Suspense fallback={<Splash />}>
        <AppRouter
          usuario={usuario}
          motivoCierre={motivoCierre}
          inicio={inicio}
          onInicioUsado={() => setInicio(ARRANQUE_NORMAL)}
          onLogin={handleLogin}
          onLogout={handleLogout}
        />
      </Suspense>
      <Toaster />
      {salida && (
        <ConfirmModal
          title="Cerrar sesión"
          message={textoSalida(salida.tuyas)}
          confirmLabel="Salir"
          confirmVariant="warning"
          onConfirm={() => salir(salida.alSalir)}
          onClose={() => setSalida(null)}
        />
      )}
    </BrowserRouter>
  );
}
